import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { type ReactNode, useEffect, useMemo, useState } from 'react'

import { iamService } from '@/api/services'
import { getHighestRole } from '@ankor-io/common/domain/iam/account'
import { AccountPreferenceKeys, type AccountPreferences, Role } from '@ankor-io/common/types/account'

import { UserContext, type UserContextType } from './UserContext'

export function UserContextProvider({ children }: { children: ReactNode }) {
  // get kindeUser details
  const { user: kindeUser, isAuthenticated } = useKindeAuth()

  const [isLoading, setIsLoading] = useState(true)
  const [user, setUser] = useState<UserContextType | null>(null)
  const [activeOrganizationId, setActiveOrganizationId] = useState<string | null>(null)
  const [activeConnectedAccountId, setActiveConnectedAccountId] = useState<string | null>(null)

  const activeOrganizationRole: Role = useMemo(() => {
    return activeOrganizationId && user?.organizations[activeOrganizationId]
      ? getHighestRole(user?.organizations[activeOrganizationId].roles) || Role.VIEWER
      : Role.VIEWER
  }, [user, activeOrganizationId])

  useEffect(() => {
    async function fetchApiUser() {
      try {
        setIsLoading(true)
        if (kindeUser) {
          const userData = {
            id: kindeUser.id,
            givenName: kindeUser?.givenName || '',
            familyName: kindeUser?.familyName || '',
            email: kindeUser?.email || '',
            organizations: {},
            preferences: {} as AccountPreferences,
            permissions: [] as string[],
          }

          try {
            // Fetch user account data from IAM service
            const accountData = await iamService.getMe()

            userData.organizations = accountData.organizations
            userData.preferences = accountData.preferences || {}

            // If the user has an active organization inside preference, use it, otherwise use the first organization
            const firstOrgId =
              userData.preferences?.[AccountPreferenceKeys.ACTIVE_ORGANIZATION_ID] ||
              Object.keys(accountData.organizations)[0]
            setActiveOrganizationId(firstOrgId)

            // If the user has a active connected account inside preference, use it
            const activeConnectedAccountId =
              userData.preferences?.[AccountPreferenceKeys.ACTIVE_CONNECTED_ACCOUNT_ID] || null
            setActiveConnectedAccountId(activeConnectedAccountId)
          } catch (error) {
            console.error('Failed to fetch account data', error)
          }
          setUser(userData)
        }

        setIsLoading(false)
      } catch (error) {
        console.error(error)
      }
    }

    if (!isAuthenticated) {
      setUser(null)
      setActiveOrganizationId(null)
      return
    }

    // Only fetch when authenticated and user ID exists
    // Use kindeUser.id instead of full kindeUser object to avoid re-runs on object reference changes
    if (kindeUser?.id) {
      fetchApiUser()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kindeUser?.id, isAuthenticated])

  const updateOrganization = (orgId: string, name: string, description: string) => {
    if (!user || !user.organizations[orgId]) {
      return
    }

    const updatedUser = { ...user }
    const updatedOrganization = { ...updatedUser.organizations[orgId], name, description }
    updatedUser.organizations[orgId] = updatedOrganization

    setUser(updatedUser)

    // Save the active organization id to the preferences
    // Skip if this org is already the active one (avoids redundant updates during onboarding)
    if (activeOrganizationId !== orgId) {
      updatePreferences({ activeOrganizationId: orgId })
    }
  }

  const updatePreferences = async (updatedPreferences: AccountPreferences) => {
    try {
      // Update preferences using IAM service
      await iamService.updatePreferences(updatedPreferences)

      // The type error occurs because spreading user creates a new object that loses the type information
      // We need to explicitly cast it back to UserContextType to maintain the type safety
      const updatedUser: UserContextType = {
        ...user!,
        preferences: { ...user!.preferences, ...updatedPreferences },
      }

      setUser(updatedUser)
    } catch (error) {
      console.error('Failed to update preferences', error)
    }
  }

  const updateActiveOrganizationId = (orgId: string) => {
    if (activeOrganizationId === orgId) {
      return
    }

    // When we update the activeOrganizationId, we need to reset the activeConnectedAccountId as well.
    // This ensures that we don't have an activeConnectedAccountId that is not associated with the activeOrganizationId
    setActiveOrganizationId(orgId)
    setActiveConnectedAccountId(null)
    updatePreferences({ activeOrganizationId: orgId, activeConnectedAccountId: '' })
  }

  const updateActiveConnectedAccountId = (connectedAccountId: string, skipPreferencesUpdate = false) => {
    if (activeConnectedAccountId === connectedAccountId) {
      return
    }

    setActiveConnectedAccountId(connectedAccountId)
    // Skip preference update if caller will handle it (during onboarding)
    if (!skipPreferencesUpdate) {
      updatePreferences({ activeConnectedAccountId: connectedAccountId })
    }
  }

  return (
    <UserContext.Provider
      value={{
        user,
        isLoading,
        organizations: user?.organizations || {},
        activeOrganizationId,
        activeConnectedAccountId,
        activeOrganizationRole,
        preferences: user?.preferences || {},
        updateOrganization,
        updateActiveOrganizationId,
        updateActiveConnectedAccountId,
        updatePreferences,
      }}
    >
      {children}
    </UserContext.Provider>
  )
}
